import { FormControl, FormHelperText, TextField } from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { cloneDeep, isEqual } from "lodash";
import { FC, useContext, useState } from "react";
import {
  Controller,
  FieldValues,
  useFormContext,
  useWatch,
} from "react-hook-form";
import {
  AppContextType,
  StateContext,
  StateContextType,
} from "../../context/ApplicationContext";
import { funcExecutor } from "../../functions/generic-functions";
import { evaluateExpression } from "../../functions/parser";
import {
  ExtendedEvents,
  FunctionDefinition,
  NameValueTuple,
} from "../model/form-configuration";
import HelperText from "./HelperText";
import { PPDatePickerProps } from "./PPField";

const PPDatePicker: FC<PPDatePickerProps> = (props) => {
  const { control, getValues, setValue } = useFormContext();
  const stateCtx: StateContextType = useContext(StateContext);

  const requiredTokens: string[] = props.extendedAttributes["required"]
    ? props.extendedAttributes["required"].split(" ")
    : [];
  const watch: FieldValues = useWatch({ control, defaultValue: undefined });
  const isRequired: boolean = evaluateExpression(requiredTokens, watch);

  const appContext = stateCtx.applicationContext;
  const setAppContext = stateCtx.setApplicationContext;

  const [dateValue, setDateValue] = useState<Date | null>(
    getValues(props.id) ?? null
  );

  const getParameter = (nameValue: NameValueTuple): any => {
    let param: any = undefined;
    switch (nameValue.type) {
      case "string":
        param = getString(nameValue);
        break;
      case "stringArray":
        param = getStringArray(nameValue);
        break;
      case "rpn":
        param = getExpression(nameValue);
        break;
      default:
        param = nameValue.value;
    }
    return param;
  };

  const getString = (nameValue: NameValueTuple): string => nameValue.value;

  const getExpression = (nameValue: NameValueTuple): string => nameValue.value;

  const getStringArray = (nameValue: NameValueTuple): string[] => {
    const stringArrRegEx: RegExp = /\[(.*?)\]/;
    const stringVals: RegExpMatchArray | null = RegExp(stringArrRegEx).exec(
      nameValue.value
    );
    if (stringVals !== null) {
      return stringVals[1].split(",");
    } else return [] as string[];
  };

  const onChange = (appContext: AppContextType) => {
    const events: ExtendedEvents | undefined = props.extendedEvents;

    if (events?.onChange) {
      const functions: FunctionDefinition[] = events.onChange;
      const sortedFunctions: FunctionDefinition[] = functions
        .slice()
        .sort(
          (a: FunctionDefinition, b: FunctionDefinition) => a.order - b.order
        );

      sortedFunctions.forEach((func: FunctionDefinition) => {
        const sortedParams: NameValueTuple[] = func.parameters
          .slice()
          .sort((a: NameValueTuple, b: NameValueTuple) => a.order - b.order);

        funcExecutor(
          func.name,
          sortedParams.map((nameValue: NameValueTuple): any =>
            getParameter(nameValue)
          ),
          getValues,
          setValue,
          appContext
        );
      });
    }
  };

  return (
    <Controller
      render={({ field }) => {
        const isFieldInvalid: boolean =
          appContext.fieldState[field.name] !== undefined &&
          !appContext.fieldState[field.name];
        const isHideField: boolean =
          appContext.fieldHide[field.name] !== undefined &&
          appContext.fieldHide[field.name];
        const isDisabledField: boolean =
          appContext.fieldDisable[field.name] !== undefined &&
          appContext.fieldDisable[field.name];

        return (
          <FormControl error={isFieldInvalid} fullWidth>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label={props.label + (isRequired ? " *" : "")}
                value={dateValue}
                onChange={(newValue, keyboardInputValue) => {
                  setDateValue(newValue);
                  //   console.log(newValue);
                  field.onChange(
                    newValue !== null && newValue.toString() !== "Invalid Date"
                      ? format(newValue, "MM/dd/uuuu")
                      : null
                  );
                  console.log(keyboardInputValue);
                  console.log(newValue);
                  //   console.log(isMatch(keyboardInputValue ?? "", "MM/dd/yyyy"));
                  if (
                    (keyboardInputValue !== null &&
                      keyboardInputValue !== undefined &&
                      keyboardInputValue.trim() !== "" &&
                      /(0[1-9]|1[012])[- /.](0[1-9]|[12]\d|3[01])[- /.](19|20)\d\d/.test(
                        keyboardInputValue ?? ""
                      )) ||
                    (newValue !== null &&
                      newValue.toString() !== "Invalid Date")
                  ) {
                    const newAppContext: AppContextType = cloneDeep(appContext);
                    onChange(newAppContext);
                    if (!isEqual(stateCtx.applicationContext, newAppContext))
                      setAppContext(newAppContext);
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    sx={isHideField ? { display: "none" } : null}
                    {...params}
                    id={props.id}
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </LocalizationProvider>
            <FormHelperText sx={isHideField ? { display: "none" } : null}>
              {isFieldInvalid ? (
                "Field is required"
              ) : (
                <HelperText helperText={props.helperText}></HelperText>
              )}
            </FormHelperText>
          </FormControl>
        );
      }}
      name={props.id}
      control={control}
    />
  );
};

export default PPDatePicker;
//PPDatePicker.whyDidYouRender = true;
